import React, { useState, useRef, useCallback } from 'react';
import { Upload } from 'lucide-react';
import { toast } from "@/lib/toast";

export interface FileMetadata {
  id: string;
  file: File;
  preview?: string;
  displayName: string;
  error?: string;
}

// File validation constants
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_FILES_COUNT = 20;

interface FileDropZoneProps {
  /** Supported file types (MIME types) */
  acceptedFileTypes: string[];
  /** Callback when files are dropped and ready for upload */
  onFileDrop: (files: File[]) => void;
  /** Callback when files are processed and ready for preview/upload */
  onFilesReady?: (files: FileMetadata[]) => void;
  /** Children to wrap */
  children: React.ReactNode;
  /** Whether the drop zone is disabled */
  disabled?: boolean;
  /** Custom text to display in overlay */
  overlayText?: string;
  /** Additional CSS classes for the wrapper */
  className?: string;
  /** Whether to disable click to open file dialog */
  disableClick?: boolean;
}

/**
 * File Drop Zone Component with modern animations and file preview
 * 
 * Wraps any content and provides drag & drop functionality for files.
 * Shows animated overlay and file preview dialog.
 */
export const FileDropZone: React.FC<FileDropZoneProps> = ({
  acceptedFileTypes,
  onFileDrop,
  onFilesReady,
  children,
  disabled = false,
  overlayText = 'Dateien hier ablegen',
  className = '',
  disableClick = true
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Formats accepted file types for display
   */
  const formatAcceptedTypes = useCallback((): string => {
    const typeMap: Record<string, string> = {
      'image/*': 'Bilder',
      'video/*': 'Videos',
      'application/pdf': 'PDF',
      'text/*': 'Text-Dateien',
      'audio/*': 'Audio'
    };

    const formatted = acceptedFileTypes
      .filter(type => type && typeof type === 'string') // Filter out undefined/null values
      .map(type => {
        if (typeMap[type]) {
          return typeMap[type];
        }
        // Handle specific MIME types
        if (type.startsWith('image/')) return 'Bilder';
        if (type.startsWith('video/')) return 'Videos';
        if (type.startsWith('audio/')) return 'Audio';
        if (type.startsWith('text/')) return 'Text';
        return type;
      });

    // Remove duplicates and join
    return Array.from(new Set(formatted)).join(', ');
  }, [acceptedFileTypes]);

  /**
   * Generates preview URL for image files
   */
  const generatePreview = useCallback((file: File): Promise<string | undefined> => {
    return new Promise((resolve, reject) => {
      if (!file || !file.type || !file.type.startsWith('image/')) {
        resolve(undefined);
        return;
      }

      try {
        const reader = new FileReader();
        
        reader.onload = (e) => {
          const result = e.target?.result as string;
          if (result) {
            resolve(result);
          } else {
            reject(new Error('Fehler beim Laden der Bildvorschau'));
          }
        };
        
        reader.onerror = () => {
          reject(new Error('Fehler beim Lesen der Datei'));
        };
        
        reader.readAsDataURL(file);
      } catch (error) {
        reject(error);
      }
    });
  }, []);


  /**
   * Checks if a file type is accepted
   */
  const isFileTypeAccepted = useCallback((fileType: string): boolean => {
    if (!fileType || typeof fileType !== 'string') return false;
    
    return acceptedFileTypes
      .filter(acceptedType => acceptedType && typeof acceptedType === 'string')
      .some(acceptedType => {
        if (acceptedType.includes('*')) {
          const baseType = acceptedType.split('/')[0];
          return fileType.startsWith(baseType + '/');
        }
        return fileType === acceptedType;
      });
  }, [acceptedFileTypes]);

  /**
   * Formats file size for display
   */
  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  /**
   * Validates a single file
   */
  const validateFile = useCallback((file: File): string | null => {
    // Check if file exists
    if (!file) {
      return 'Ungültige Datei';
    }

    // Check file size
    if (typeof file.size !== 'number' || file.size > MAX_FILE_SIZE) {
      return `Datei zu groß (max. ${formatFileSize(MAX_FILE_SIZE)})`;
    }

    // Check file type
    if (!file.type || !isFileTypeAccepted(file.type)) {
      return `Dateityp nicht unterstützt`;
    }

    // Check file name
    if (!file.name || typeof file.name !== 'string' || file.name.trim().length === 0) {
      return `Dateiname ist erforderlich`;
    }

    if (file.name.length > 255) {
      return `Dateiname zu lang (max. 255 Zeichen)`;
    }

    return null;
  }, [isFileTypeAccepted, formatFileSize]);

  /**
   * Filters files to only include supported types
   */
  const filterValidFiles = useCallback((files: FileList): File[] => {
    return Array.from(files).filter(file => 
      isFileTypeAccepted(file.type)
    );
  }, [isFileTypeAccepted]);

  /**
   * Handle drag enter event
   */
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (disabled) return;
    
    // Check if any files are being dragged
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      const hasFiles = Array.from(e.dataTransfer.items).some(item => item.kind === 'file');
      if (hasFiles) {
        setIsDragOver(true);
      }
    }
  }, [disabled]);



  /**
   * Handle drag over event
   */
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (disabled) return;

    // Set the drop effect
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      const hasFiles = Array.from(e.dataTransfer.items).some(item => item.kind === 'file');
      e.dataTransfer.dropEffect = hasFiles ? 'copy' : 'none';
    } else {
      e.dataTransfer.dropEffect = 'copy';
    }
  }, [disabled]);

  /**
   * Handle drop event with file processing
   */
  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (disabled) return;

    setIsDragOver(false);
    setIsProcessing(true);

    try {
      const files = e.dataTransfer.files;
      
      if (!files || files.length === 0) {
        toast.error("Keine Dateien gefunden");
        setIsProcessing(false);
        return;
      }

      if (files.length > MAX_FILES_COUNT) {
        toast.error(`Zu viele Dateien. Maximum: ${MAX_FILES_COUNT}`);
        setIsProcessing(false);
        return;
      }

      const validFiles = filterValidFiles(files);
      const invalidCount = files.length - validFiles.length;

      if (invalidCount > 0) {
        toast.warning(`${invalidCount} Datei(en) wurden übersprungen (ungültiger Typ oder zu groß)`);
      }

      if (validFiles.length === 0) {
        toast.error("Keine gültigen Dateien gefunden");
        setIsProcessing(false);
        return;
      }

      const filesWithMetadata: FileMetadata[] = [];
      
      for (const file of validFiles) {
        try {
          const validationError = validateFile(file);
          let preview: string | undefined;
          
          try {
            preview = await generatePreview(file);
          } catch (previewError) {
            console.warn('Preview generation failed:', previewError);
            // Continue without preview
          }

          // Create FileMetadata object keeping the original File object separate
          const fileMetadata: FileMetadata = {
            id: Math.random().toString(36).substring(2, 9),
            file: file,
            preview: preview,
            displayName: file.name || 'untitled',
            error: validationError || undefined
          };
          
          filesWithMetadata.push(fileMetadata);
        } catch (error) {
          console.error('Error processing file:', file.name, error);
          toast.error(`Datei konnte nicht verarbeitet werden`, {
            description: file.name,
            error,
          });
        }
      }

      if (filesWithMetadata.length === 0) {
        toast.error("Keine Dateien konnten verarbeitet werden");
        setIsProcessing(false);
        return;
      }

      // Call the callback to pass files to parent component
      if (onFilesReady) {
        onFilesReady(filesWithMetadata);
      }
      
      // Also call onFileDrop with the raw files for backward compatibility
      if (onFileDrop) {
        const rawFiles = filesWithMetadata.map(metadata => metadata.file);
        onFileDrop(rawFiles);
      }      
    } catch (error) {
      console.error('Drop handling error:', error);
      toast.error("Dateien konnten nicht verarbeitet werden", { error });
    } finally {
      setIsProcessing(false);
    }
  }, [disabled, filterValidFiles, generatePreview, onFileDrop, onFilesReady, validateFile]);



  /**
   * Handle click to open file dialog
   */
  const handleClick = useCallback(() => {
    if (disabled || disableClick) return;
    
    try {
      fileInputRef.current?.click();
    } catch (error) {
      console.error('Error opening file dialog:', error);
      toast.error('Datei-Dialog konnte nicht geöffnet werden', { error });
    }
  }, [disabled, disableClick]);

  /**
   * Handle file input change
   */
  const handleFileInputChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsProcessing(true);

    try {
      if (files.length > MAX_FILES_COUNT) {
        toast.error(`Zu viele Dateien. Maximum: ${MAX_FILES_COUNT}`);
        setIsProcessing(false);
        return;
      }

      const validFiles = filterValidFiles(files);
      const invalidCount = files.length - validFiles.length;

      if (invalidCount > 0) {
        toast.warning(`${invalidCount} Datei(en) wurden übersprungen (ungültiger Typ oder zu groß)`);
      }

      if (validFiles.length === 0) {
        toast.error("Keine gültigen Dateien gefunden");
        setIsProcessing(false);
        return;
      }

      const filesWithMetadata: FileMetadata[] = [];
      
      for (const file of validFiles) {
        try {
          const validationError = validateFile(file);
          let preview: string | undefined;
          
          try {
            preview = await generatePreview(file);
          } catch (previewError) {
            console.warn('Preview generation failed:', previewError);
          }

          const fileMetadata: FileMetadata = {
            id: Math.random().toString(36).substring(2, 9),
            file: file,
            preview,
            displayName: file.name,
            error: validationError || undefined
          };
          
          filesWithMetadata.push(fileMetadata);
        } catch (error) {
          console.error('Error processing file:', file.name, error);
          toast.error(`Datei konnte nicht verarbeitet werden`, {
            description: file.name,
            error,
          });
        }
      }

      if (filesWithMetadata.length === 0) {
        toast.error("Keine Dateien konnten verarbeitet werden");
        setIsProcessing(false);
        return;
      }

      // Call the callback to pass files to parent component
      if (onFilesReady) {
        onFilesReady(filesWithMetadata);
      }
      
      // Also call onFileDrop with the raw files for backward compatibility
      if (onFileDrop) {
        const rawFiles = filesWithMetadata.map(metadata => metadata.file);
        onFileDrop(rawFiles);
      }
      
      toast.success(`${filesWithMetadata.length} Datei(en) bereit zum Upload`);
      
    } catch (error) {
      console.error('File input handling error:', error);
      toast.error("Dateien konnten nicht verarbeitet werden", { error });
    } finally {
      setIsProcessing(false);
      // Reset file input
      if (e.target) {
        e.target.value = '';
      }
    }
  }, [filterValidFiles, generatePreview, onFileDrop, onFilesReady, validateFile]);
  if (disabled) {
    return <>{children}</>;
  }

  return (
    <>
      <div
        ref={dropZoneRef}
        className={`relative transition-all duration-200 ${className} ${
          disableClick
            ? 'cursor-default'
            : 'cursor-pointer hover:bg-gray-50/50'
        }`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedFileTypes.join(',')}
          onChange={handleFileInputChange}
          className="hidden"
        />
        
        {/* Original content */}
        {children}
        
        {/* Modern minimalist overlay when dragging */}
        {isDragOver && (
          <div className="absolute inset-0 bg-white/95 backdrop-blur-sm flex items-center justify-center z-50 pointer-events-none animate-in fade-in duration-200">
            <div className="text-center relative z-10">
              <div className="mb-6">
                <div className="w-16 h-16 mx-auto bg-blue-50 rounded-2xl flex items-center justify-center border border-blue-100 shadow-sm">
                  <Upload className="h-8 w-8 text-blue-600" />
                </div>
              </div>
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-900" style={{ textShadow: '0 0 10px white, 0 0 20px white, 0 0 30px white, 0 0 40px white, 0 0 50px white, 0 0 60px white, 0 0 70px white, 0 0 80px white, 0 0 90px white, 0 0 100px white' }}>{overlayText}</h3>
                <p className="text-sm text-gray-600 max-w-xs" style={{ textShadow: '0 0 10px white, 0 0 20px white, 0 0 30px white, 0 0 40px white, 0 0 50px white, 0 0 60px white, 0 0 70px white, 0 0 80px white, 0 0 90px white, 0 0 100px white' }}>
                  {formatAcceptedTypes()}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Processing overlay */}
        {isProcessing && (
          <div className="absolute inset-0 bg-white/90 backdrop-blur-md flex items-center justify-center z-50 pointer-events-none animate-in fade-in duration-200 rounded-xl">
            <div className="text-center bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-gray-200 shadow-xl">
              <div className="relative mb-6">
                <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
              </div>
              <div className="space-y-2">
                <p className="text-gray-900 font-semibold text-lg">Dateien werden verarbeitet</p>
                <p className="text-gray-500 text-sm">Bitte warten Sie einen Moment...</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

/**
 * Hook for common file type configurations
 */
export const useFileTypeConfigs = () => {
  return {
    images: [
      'image/jpeg', 
      'image/jpg', 
      'image/png', 
      'image/webp', 
      'image/gif', 
      'image/svg+xml',
      'image/avif',
      'image/heic',
      'image/heif',
      'image/heic-sequence',
      'image/heif-sequence'
    ],
    videos: ['video/mp4', 'video/webm', 'video/ogg'],
    documents: ['application/pdf', 'text/plain', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    all: [
      'image/jpeg', 
      'image/jpg', 
      'image/png', 
      'image/webp', 
      'image/gif', 
      'image/svg+xml', 
      'image/avif',
      'image/heic',
      'image/heif',
      'image/heic-sequence',
      'image/heif-sequence',
      'video/mp4', 
      'video/webm', 
      'video/ogg', 
      'application/pdf', 
      'text/plain'
    ]
  };
};
